import { useCallback, useEffect, useState } from 'react';
import { IconAlertTriangle, IconExternalLink, IconLoader2 } from '@tabler/icons-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dxBoardAPI } from '../../api/client';
import { COLORS, RADIUS, SHADOW, badge } from '../../utils/uiStyles';
import { safeUrl } from '../../utils/safeUrl';

const CHART = {
  primary: COLORS.primary,
  done: '#0d9488',
  open: '#f59e0b',
  danger: COLORS.danger,
  neutral: '#64748b',
  grid: COLORS.borderSoft,
};

const sectionStyle = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.borderSoft}`,
  borderRadius: RADIUS.lg,
  padding: '18px',
};

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatMetric(value, suffix = '') {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toLocaleString()}${suffix}`;
}

function formatDate(value) {
  if (!value) return '-';
  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (parts) {
    const [, year, month, day] = parts;
    return year === '2026' ? `${month}.${day}` : `${year.slice(2)}.${month}.${day}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() === 2026 ? `${mm}.${dd}` : `${yy}.${mm}.${dd}`;
}

function formatSyncedAt(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function EmptyChart() {
  return (
    <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
      표본 없음
    </div>
  );
}

function ChartFrame({ title, subtitle, children }) {
  return (
    <section style={sectionStyle}>
      <div style={{ marginBottom: '14px' }}>
        <h3 style={{ margin: 0, color: COLORS.text, fontSize: '15px', fontWeight: 620 }}>{title}</h3>
        {subtitle && <div style={{ marginTop: '4px', color: COLORS.textSub, fontSize: '12px', lineHeight: 1.5 }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function KpiCard({ label, value, meta, tone }) {
  const color = tone === 'danger' ? COLORS.dangerText : tone === 'success' ? COLORS.successText : COLORS.text;
  const bg = tone === 'danger' ? COLORS.dangerSoft : tone === 'success' ? COLORS.successSoft : COLORS.surface;
  return (
    <div style={{ background: bg, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.lg, padding: '16px', minWidth: 0 }}>
      <div style={{ color: COLORS.textSub, fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>{label}</div>
      <div style={{ color, fontSize: '24px', fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div style={{ color: COLORS.textSub, fontSize: '12px', marginTop: '8px' }}>{meta}</div>
    </div>
  );
}

function BasicTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const displayLabel = payload[0]?.payload?.name || label;
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.lg, padding: '8px 10px', fontSize: '12px', color: COLORS.text, boxShadow: SHADOW.float }}>
      <div style={{ fontWeight: 620, marginBottom: '4px' }}>{displayLabel}</div>
      {payload.map((item) => (
        <div key={item.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', lineHeight: 1.6 }}>
          <span style={{ color: item.color }}>{item.name}</span>
          <b>{formatNumber(item.value)}{String(item.name || '').includes('율') ? '%' : ''}</b>
        </div>
      ))}
    </div>
  );
}

function orgBadgeMeta(org) {
  if (org === '아이니(외주)') {
    return { label: '아이니', style: badge('blue') };
  }
  if (org === '아워박스(내부)') {
    return { label: '내부', style: badge('green') };
  }
  if (org === '기타') {
    return { label: '기타', style: badge('blue') };
  }
  return { label: '미확인', style: badge('yellow') };
}

function OrgBadge({ org, role }) {
  const meta = orgBadgeMeta(org);
  const label = role ? `${meta.label} · ${role}` : meta.label;
  return (
    <span style={{ ...meta.style }}>
      {label}
    </span>
  );
}

function orgRowPriority(org) {
  if (org === '아이니(외주)') return 0;
  if (org === '아워박스(내부)') return 1;
  if (org === '기타') return 2;
  if (org === '미확인') return 3;
  return 2;
}

function AssigneeAxisTick({ x, y, payload, rowsByKey }) {
  const row = rowsByKey?.[payload?.value] || {};
  const name = row.name || payload?.value || '';
  const title = row.role ? `${name} · ${row.org || '-'} · ${row.role}` : name;
  return (
    <foreignObject x={x - 158} y={y - 12} width={152} height={24}>
      <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '5px', height: '24px', overflow: 'hidden' }}>
        <span title={title} style={{ color: COLORS.textSub, fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        <OrgBadge org={row.org} role={row.role} />
      </div>
    </foreignObject>
  );
}

function TableSection({ title, subtitle, columns, rows, renderRow }) {
  return (
    <section style={sectionStyle}>
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ margin: 0, color: COLORS.text, fontSize: '15px', fontWeight: 620 }}>{title}</h3>
        {subtitle && <div style={{ marginTop: '4px', color: COLORS.textSub, fontSize: '12px' }}>{subtitle}</div>}
      </div>
      {!rows?.length ? (
        <div style={{ color: COLORS.textMuted, fontSize: '13px', padding: '28px 0', textAlign: 'center' }}>표본 없음</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px', fontSize: '13px' }}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column} style={{ textAlign: 'left', fontSize: '12px', color: COLORS.textMuted, fontWeight: 400, borderBottom: `1px solid ${COLORS.borderSoft}`, padding: '9px 8px', whiteSpace: 'nowrap' }}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TitleCell({ row }) {
  const href = safeUrl(row.url);
  const title = row.title || '(제목 없음)';
  if (!href) {
    return <span style={{ color: COLORS.text, fontWeight: 700 }}>{title}</span>;
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.text, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <span>{title}</span>
      <IconExternalLink size={13} stroke={1.8} />
    </a>
  );
}

export default function DxAnalytics({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    dxBoardAPI.getAnalytics()
      .then((r) => setData(r.data || null))
      .catch((e) => {
        setData(null);
        setError(e.response?.data?.detail || '분석 데이터를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.lg, padding: '42px', textAlign: 'center', color: COLORS.textSub, fontSize: '14px' }}>
        <IconLoader2 size={18} stroke={2} style={{ animation: 'dx-summary-spin .8s linear infinite', verticalAlign: '-4px', marginRight: '6px' }} />
        분석 데이터를 불러오는 중입니다.
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: COLORS.dangerSoft, border: `1px solid ${COLORS.dangerSoft}`, borderRadius: RADIUS.lg, padding: '28px', textAlign: 'center', color: COLORS.dangerText, fontSize: '14px' }}>
        {error}
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const schedule = kpi.schedule || {};
  const devLead = kpi.dev_lead || {};
  const monthly = data?.monthly || [];
  const delayBuckets = data?.delay_buckets || [];
  const slackBuckets = data?.slack_buckets || [];
  const slackSummary = data?.slack_summary || {};
  const assigneeRows = data?.by_assignee || [];
  const orgRows = [...(data?.by_org || [])].sort((a, b) => orgRowPriority(a.org) - orgRowPriority(b.org));
  const statusRows = data?.by_status || [];
  const systemRows = data?.by_system || [];
  const assigneeChartRows = assigneeRows.map((row, index) => ({ ...row, _axisKey: `${row.name || 'assignee'}-${index}` }));
  const assigneeRowsByKey = assigneeChartRows.reduce((acc, row) => {
    acc[row._axisKey] = row;
    return acc;
  }, {});
  const hasMonthly = monthly.some((row) => row.closed || row.n);
  const hasDelay = Number(schedule.n || 0) > 0;
  const hasSlack = Number(slackSummary.n || 0) > 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(190px, 100%), 1fr))', gap: '12px' }}>
        <KpiCard label="진행 중 물량" value={`${formatNumber(kpi.open_count)}건`} meta={`n=${formatNumber(kpi.open_count)}`} />
        <KpiCard label="일정 준수율" value={`${formatMetric(schedule.rate, '%')}`} meta={`n=${formatNumber(schedule.n)} · 준수 ${formatNumber(schedule.kept)} / 지연 ${formatNumber(schedule.delayed)}`} tone={schedule.rate < 50 ? 'danger' : 'success'} />
        <KpiCard label="개발 리드타임 중앙값" value={formatMetric(devLead.median, '일')} meta={`n=${formatNumber(devLead.n)} · p90 ${formatMetric(devLead.p90, '일')}`} />
        <KpiCard label="이번 달 완료" value={`${formatNumber(kpi.closed_this_month)}건`} meta={`n=${formatNumber(kpi.closed_this_month)}`} />
      </div>

      <TableSection
        title="조직별 현황"
        subtitle="개발자 이메일 도메인 기준 · 한 카드에 여러 조직이 있으면 각 조직에 1건씩 계상합니다."
        columns={['조직', '인원', '전체', '진행중', '완료', '일정 준수율', '개발 리드타임']}
        rows={orgRows}
        renderRow={(row) => {
          const scheduleRow = row.schedule || {};
          const leadRow = row.dev_lead || {};
          const isAini = row.org === '아이니(외주)';
          const isSecondary = row.org === '기타' || row.org === '미확인';
          const orgTitle = row.org === '미확인' ? '노션에서 이름·이메일이 조회되지 않는 계정(퇴사·비활성 추정) 6명' : undefined;
          return (
            <tr key={row.org} style={{ background: isAini ? COLORS.primarySoft : 'transparent', opacity: isSecondary ? 0.68 : 1 }}>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, fontWeight: isAini ? 700 : 620, color: COLORS.text, whiteSpace: 'nowrap' }}>
                <span title={orgTitle} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                  {row.org || '-'}
                  <OrgBadge org={row.org} />
                </span>
              </td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatNumber(row.people)}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatNumber(row.total)}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatNumber(row.open)}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatNumber(row.done)}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>
                <b>{formatMetric(scheduleRow.rate, '%')}</b>
                <span style={{ color: COLORS.textSub }}> · n={formatNumber(scheduleRow.n)}</span>
              </td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>
                {formatMetric(leadRow.median, '일')}
                <span style={{ color: COLORS.textSub }}> · n={formatNumber(leadRow.n)}</span>
              </td>
            </tr>
          );
        }}
      />

      <ChartFrame title="② 일정 · 월별 완료 건수 + 준수율 추이" subtitle={`최근 12개월 · 준수율은 QA완료 예정일과 실제 완료일이 있는 완료 카드 기준입니다. n=${formatNumber(schedule.n)}`}>
        {!hasMonthly ? <EmptyChart /> : (
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthly} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                <Tooltip content={<BasicTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="closed" name="완료건수" fill={CHART.done} radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="rate" name="준수율" stroke={CHART.primary} strokeWidth={2.4} dot={{ r: 3, fill: CHART.primary }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartFrame>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '16px' }}>
        <ChartFrame title="② 일정 · 지연 구간 분포" subtitle={`지연 중앙값 ${formatMetric(schedule.median_delay_days, '일')} · 최대 ${formatMetric(schedule.max_delay_days, '일')} · n=${formatNumber(schedule.n)}`}>
          {!hasDelay ? <EmptyChart /> : (
            <div style={{ height: '280px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={delayBuckets} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <Tooltip content={<BasicTooltip />} />
                  <Bar dataKey="n" name="카드수" fill={CHART.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartFrame>

        <ChartFrame title="③ 과대추정 · 여유율 분포" subtitle={`여유율 중앙값 ${formatMetric(slackSummary.median_slack, '배')} · n=${formatNumber(slackSummary.n)} · 1 미만은 계획 초과, 2 이상은 과대입니다.`}>
          {!hasSlack ? <EmptyChart /> : (
            <div style={{ height: '280px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={slackBuckets} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <Tooltip content={<BasicTooltip />} />
                  <Bar dataKey="n" name="카드수" radius={[4, 4, 0, 0]}>
                    {slackBuckets.map((entry) => (
                      <Cell key={entry.label} fill={entry.label === '과대(2배+)' ? CHART.danger : CHART.neutral} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartFrame>
      </div>

      <ChartFrame title="④ 누가 얼만큼 · 개발자별 물량" subtitle="상위 10명 · 한 카드에 개발자가 여러 명이면 각 개발자에게 1건씩 계상합니다.">
        {!assigneeRows.length ? <EmptyChart /> : (
          <div style={{ height: `${Math.max(300, assigneeRows.length * 34 + 48)}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={assigneeChartRows} layout="vertical" margin={{ top: 8, right: 12, left: 18, bottom: 0 }}>
                <CartesianGrid stroke={CHART.grid} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="_axisKey" width={160} tick={<AssigneeAxisTick rowsByKey={assigneeRowsByKey} />} axisLine={false} tickLine={false} />
                <Tooltip content={<BasicTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="open" name="진행중" stackId="work" fill={CHART.open} radius={[0, 4, 4, 0]} />
                <Bar dataKey="done" name="완료" stackId="work" fill={CHART.done} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartFrame>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '16px' }}>
        <ChartFrame title="① 전반 · 상태별 현황" subtitle={`전체 동기화 카드 n=${formatNumber(data?.total_count)}`}>
          {!statusRows.length ? <EmptyChart /> : (
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusRows} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="status" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <Tooltip content={<BasicTooltip />} />
                  <Bar dataKey="n" name="카드수" fill={CHART.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartFrame>

        <ChartFrame title="① 전반 · 시스템별 현황" subtitle="샵메이트 1.0 / 2.0 multi-select 기준 · 완료와 전체를 함께 봅니다.">
          {!systemRows.length ? <EmptyChart /> : (
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={systemRows} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="system" tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.textSub }} axisLine={false} tickLine={false} />
                  <Tooltip content={<BasicTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="total" name="전체" fill={CHART.neutral} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="done" name="완료" fill={CHART.done} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartFrame>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: '16px' }}>
        <TableSection
          title="지연 TOP 10"
          subtitle="QA완료 예정일 대비 실제 완료일 지연이 큰 완료 카드입니다."
          columns={['제목', '개발자', '예정일', '완료일', '지연']}
          rows={data?.worst_delays || []}
          renderRow={(row, idx) => (
            <tr key={`${row.url || row.title || 'delay'}-${idx}`}>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, maxWidth: '260px', wordBreak: 'break-word' }}><TitleCell row={row} /></td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textSub }}>{(row.assignees || []).join(', ') || '-'}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatDate(row.qa_done_at)}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatDate(row.closed_at)}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.dangerText, fontWeight: 620, whiteSpace: 'nowrap' }}>{formatNumber(row.delay_days)}일</td>
            </tr>
          )}
        />

        <TableSection
          title="과대추정 TOP 10"
          subtitle="계획기간 / 실제 개발소요가 2배 이상인 카드입니다."
          columns={['제목', '계획일', '실제일', '여유배수']}
          rows={data?.over_planned || []}
          renderRow={(row, idx) => (
            <tr key={`${row.url || row.title || 'slack'}-${idx}`}>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, maxWidth: '280px', wordBreak: 'break-word' }}><TitleCell row={row} /></td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatMetric(row.plan_days, '일')}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' }}>{formatMetric(row.actual_days, '일')}</td>
              <td style={{ padding: '9px 8px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.dangerText, fontWeight: 620, whiteSpace: 'nowrap' }}>{formatMetric(row.slack, '배')}</td>
            </tr>
          )}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: COLORS.textSub, background: COLORS.warnSoft, borderRadius: RADIUS.lg, padding: '12px 14px', fontSize: '12px', lineHeight: 1.6 }}>
        <IconAlertTriangle size={16} stroke={2} style={{ color: COLORS.warn, flexShrink: 0, marginTop: '1px' }} />
        <span>
          동기화 {formatSyncedAt(data?.synced_at)} · 전체 n={formatNumber(data?.total_count)} · 일정 준수율 n={formatNumber(schedule.n)}.
          예정일이 갱신되지 않은 카드가 섞여 있을 수 있어 준수율은 하한으로 보십시오.
        </span>
      </div>
    </div>
  );
}
